import * as React from "react";
import { Category } from "../types";
import useApi from "@/hooks/useApi";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from "@velaro/velaro-component-lib";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";

interface Props {
  category: Category;
  onClose(): void;
  onSubmit(category: Category): void;
}

const INPUT_CLASS =
  "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const LABEL_CLASS = "block text-sm font-medium text-slate-700 mb-1";

const ICON_OPTIONS = [
  { icon: "fa-solid fa-folder-open", label: "Folder" },
  { icon: "fa-solid fa-play", label: "Get Started" },
  { icon: "fa-solid fa-robot", label: "AI / Bot" },
  { icon: "fa-solid fa-plug", label: "Integrations" },
  { icon: "fa-solid fa-comments", label: "Channels" },
  { icon: "fa-solid fa-chart-bar", label: "Analytics" },
  { icon: "fa-solid fa-credit-card", label: "Billing" },
  { icon: "fa-solid fa-users", label: "Team" },
  { icon: "fa-solid fa-shield-halved", label: "Security" },
  { icon: "fa-solid fa-diagram-project", label: "Workflows" },
  { icon: "fa-solid fa-book-open", label: "Knowledge Base" },
  { icon: "fa-solid fa-address-book", label: "Contacts" },
  { icon: "fa-solid fa-code", label: "Developer" },
  { icon: "fa-solid fa-mobile-screen", label: "Mobile" },
  { icon: "fa-solid fa-phone", label: "Voice / IVR" },
  { icon: "fa-solid fa-star-half-stroke", label: "Surveys" },
  { icon: "fa-solid fa-gear", label: "Settings" },
  { icon: "fa-solid fa-bell", label: "Notifications" },
  { icon: "fa-solid fa-headset", label: "Support" },
  { icon: "fa-solid fa-lock", label: "Security / Auth" },
  { icon: "fa-solid fa-envelope", label: "Email" },
  { icon: "fa-solid fa-message", label: "Messaging" },
  { icon: "fa-solid fa-magnifying-glass", label: "Search" },
  { icon: "fa-solid fa-rocket", label: "Launch" },
  { icon: "fa-solid fa-lightbulb", label: "Tips" },
  { icon: "fa-solid fa-wrench", label: "Tools" },
  { icon: "fa-solid fa-database", label: "Data" },
  { icon: "fa-solid fa-globe", label: "Web" },
  { icon: "fa-solid fa-tag", label: "Tags" },
  { icon: "fa-solid fa-list-check", label: "Checklist" },
];

export default function CategoryEditModal(props: Props) {
  const [saving, setSaving] = React.useState(false);
  const [category, setCategory] = React.useState<Category>(props.category);
  const api = useApi();

  const handleSubmit = React.useCallback(async () => {
    setSaving(true);
    try {
      const response = await api.admin.put("api/kb/categories", category);
      const json = await response.json();
      props.onSubmit(json);
    } catch (err) {
      console.error("Failed to update category:", err);
    } finally {
      setSaving(false);
    }
  }, [category, props]);

  const selectedIcon = category.Icon ?? "";

  return (
    <Modal show={true}>
      <ModalHeader title="Edit Category" onClose={() => props.onClose()} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <ModalBody>
          <div className="space-y-5">
            <div>
              <label htmlFor="category-name" className={LABEL_CLASS}>Name</label>
              <input
                autoFocus
                id="category-name"
                type="text"
                className={INPUT_CLASS}
                value={category.Name}
                onChange={(e) =>
                  setCategory({ ...category, Name: e.currentTarget.value })
                }
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Icon</label>
              <div className="grid grid-cols-6 gap-2 mt-1 max-h-52 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {ICON_OPTIONS.map(({ icon, label }) => (
                  <button
                    key={icon}
                    type="button"
                    title={label}
                    onClick={() =>
                      setCategory({
                        ...category,
                        Icon: selectedIcon === icon ? null : icon
                      })
                    }
                    className={[
                      "flex flex-col items-center justify-center gap-1 p-2 rounded-lg border text-xs transition-colors",
                      selectedIcon === icon
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-transparent hover:border-slate-300 hover:bg-slate-50 text-slate-600"
                    ].join(" ")}
                  >
                    <i className={`${icon} text-lg`} />
                    <span className="truncate w-full text-center leading-tight">{label}</span>
                  </button>
                ))}
              </div>
              {selectedIcon && (
                <button
                  type="button"
                  className="mt-1 text-xs text-slate-500 hover:text-slate-700 underline"
                  onClick={() => setCategory({ ...category, Icon: null })}
                >
                  Use auto-detected icon
                </button>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <SecondaryBlueButton
            label="Cancel"
            disabled={saving}
            onClick={() => props.onClose()}
          />
          <PrimaryBlueButton
            label={saving ? "Saving…" : "Save Category"}
            disabled={saving}
            onClick={() => handleSubmit()}
          />
        </ModalFooter>
      </form>
    </Modal>
  );
}
